import classNames from 'classnames'
import React from 'react'

import { NicknameModal, NicknameModalProps } from '../../modules/nickname-modal/nickname-modal.js'
import { Button, ButtonProps } from '../button/button.js'
import { Copy, CopyProps } from '../copy/copy.js'
import { FlexLogo } from '../flex-logo/flex-logo.js'
import { GridColumn } from '../grid/grid-column.js'
import { Grid } from '../grid/grid.js'
import { useCurrentBreakPoint } from '../helper/hooks/use-current-breakpoint.js'
import { ChevronLeftLargeIcon, CloseIcon, EditIcon, PlusIcon } from '../icon/icon.js'
import { Image, ImageProps } from '../image/image.js'
import { List, ListItem, ListItemProps } from '../list/list.js'
import {
    Modal,
    ModalCloser,
    ModalContent,
    ModalHeader,
    ModalProps,
    ModalProvider,
    ModalProviderProps,
    ModalTrigger,
} from '../modal/modal.js'
import { Separator } from '../separator/separator.js'
import { Tag, TagProps } from '../tag/tag.js'

export interface SubscriptionProps {
    title: string
    imei?: string
    copy?: CopyProps
    image?: ImageProps
    actions?: SubscriptionActionProps[]
    className?: string
    description?: string
    isDetail?: boolean
    listItems?: ListItemProps[]
    modalTriggerDefaultText?: string
    modalTriggerEditText?: string
    nicknameModal?: NicknameModalProps
    value?: string
    tagValue?: string
    claimTag?: TagProps
    hasNickname?: boolean
    paymentAction?: {
        text: string
        link: string
    }
}

export interface SubscriptionModalProps extends ModalProps {
    closeButton: ButtonProps
    closeButtonMobile: ButtonProps
    triggerButton: ButtonProps
    providerProps?: Omit<ModalProviderProps, 'children'>
}

export type SubscriptionActionProps = ButtonProps | SubscriptionModalProps

export const Subscription = (props: SubscriptionProps): JSX.Element => {
    const bp = useCurrentBreakPoint()
    const isMobile = bp ? bp === 'xs' : true

    const {
        actions,
        copy,
        description,
        image,
        imei,
        isDetail,
        listItems,
        modalTriggerDefaultText,
        modalTriggerEditText,
        nicknameModal,
        title,
    } = props

    const className: string = classNames('subscription', isDetail && 'subscription--detail', props.className)

    const renderList = (items: ListItemProps[]) => (
        <>
            <Separator variant={isDetail ? 'large' : 'slim'} />
            <List
                className="text--margin-none text-small subscription__list"
                size={isDetail ? undefined : 'small'}
                type="icon"
            >
                {items.map((item: ListItemProps, index: number) => (
                    <ListItem iconClassName="text-large" {...item} key={index} />
                ))}
            </List>
            {isDetail && <Separator variant="large" />}
        </>
    )

    const isButton = (action: ButtonProps | ModalProps): action is ButtonProps =>
        (action as ButtonProps).text !== undefined

    return (
        <div className={className}>
            <Grid className="subscription__grid" gutter="mobile" noWrap={true}>
                <GridColumn width={['min-xs']}>
                    {image ? (
                        <Image {...image} className="subscription__image" />
                    ) : (
                        <FlexLogo width={isDetail ? 102 : 42} />
                    )}
                </GridColumn>
                <GridColumn className="subscription__info" width={['max-xs']} align={['center-xs']}>
                    {!isDetail && props.claimTag?.text && (
                        <div className="subscription__tag">
                            <Tag {...props.claimTag} />
                        </div>
                    )}
                    {!isDetail && props.tagValue && (
                        <div className="subscription__tag">
                            <Tag text={props.tagValue} />
                        </div>
                    )}
                    {isDetail && (
                        <>
                            {props.claimTag?.text && (
                                <div className="subscription__tag">
                                    <Tag {...props.claimTag} />
                                </div>
                            )}
                            <div className="subscription__edit">
                                {props.tagValue && (
                                    <div className="subscription__tag">
                                        <Tag text={props.tagValue} />
                                    </div>
                                )}
                                {isDetail && nicknameModal && modalTriggerDefaultText && modalTriggerEditText && (
                                    <div className="subscription__edit-modal">
                                        <NicknameModal
                                            {...nicknameModal}
                                            triggerButton={{
                                                ...nicknameModal.triggerButton,
                                                text: props.hasNickname
                                                    ? modalTriggerEditText
                                                    : modalTriggerDefaultText,
                                                iconLeft: props.hasNickname ? EditIcon : PlusIcon,
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                    <div className="subscription__text text">
                        <h3>{title}</h3>
                        {description && <p className="subscription__description text-small">{description}</p>}
                        {imei && (
                            <Grid className="subscription__imei" align={['middle-xs']} gutter="mobile" noWrap={true}>
                                <GridColumn width={['max-xs']}>
                                    <p className="text-small">{imei}</p>
                                </GridColumn>
                                {copy && (
                                    <GridColumn width={['min-xs']}>
                                        <Copy {...copy} />
                                    </GridColumn>
                                )}
                            </Grid>
                        )}
                        {!isDetail && listItems && renderList(listItems)}
                    </div>
                </GridColumn>
            </Grid>
            {!props.paymentAction && (
                <>
                    {isDetail && listItems && renderList(listItems)}
                    {actions?.map((action: SubscriptionActionProps, index: number) => {
                        if (isButton(action)) {
                            return (
                                <Button
                                    {...action}
                                    className="subscription__action"
                                    key={index}
                                    isBlock={true}
                                    variant="secondary"
                                />
                            )
                        } else {
                            const { closeButton, closeButtonMobile, triggerButton, providerProps, ...rest } = action

                            return (
                                <ModalProvider {...providerProps} key={index}>
                                    <ModalTrigger>
                                        <Button
                                            {...triggerButton}
                                            className="subscription__action"
                                            isBlock={true}
                                            variant="secondary"
                                        />
                                    </ModalTrigger>
                                    <Modal {...rest} isClosable={false} isSidebar={true}>
                                        <ModalHeader>
                                            <ModalCloser>
                                                <Button
                                                    {...(isMobile ? closeButtonMobile : closeButton)}
                                                    className="page-title__button"
                                                    iconClassName="button__icon--large"
                                                    iconLeft={isMobile ? ChevronLeftLargeIcon : CloseIcon}
                                                    style="link"
                                                />
                                            </ModalCloser>
                                        </ModalHeader>
                                        <ModalContent>{action.children}</ModalContent>
                                    </Modal>
                                </ModalProvider>
                            )
                        }
                    })}
                </>
            )}
            {props.paymentAction && (
                <Button
                    element="a"
                    url={props.paymentAction.link}
                    text={props.paymentAction.text}
                    className="subscription__action"
                    isBlock={true}
                    variant="secondary"
                    target="_blank"
                />
            )}
        </div>
    )
}
